import {
  RiskScoreSortField,
  RiskScoreSortOrder,
  type RiskScoreFacetValue,
  type RiskScoreMetricFacet,
} from '@amzn/gbsa-helios-typescript-client';
import { SortDirection } from '@amzn/stencil-react-components/table';

import {
  RISK_STATUS_MESSAGE_KEY,
  type RiskStatusMessageKey,
} from './riskScorePresentation';

/** One option rendered in a Stencil `FilterList`. */
export interface RiskFilterOption {
  readonly label: string;
  readonly value: string;
}

/** Builds a chip label from a facet name and its match count, e.g. `Quality (12)`. */
export type RiskOptionLabel = (name: string, count: number) => string;

/**
 * Table column id → server sort key. Only these four columns are server-sortable; location,
 * actual, target, and unit have no `RiskScoreSortField` and are therefore not marked sortable.
 */
export const RISK_SORT_FIELD_BY_COLUMN_ID: Readonly<Record<string, RiskScoreSortField>> =
  {
    category: RiskScoreSortField.CATEGORY,
    metric: RiskScoreSortField.METRIC,
    status: RiskScoreSortField.STATUS,
    gap: RiskScoreSortField.GAP_TO_TARGET,
  };

/** The column ids that render a sort affordance. */
export const RISK_SORTABLE_COLUMN_IDS: ReadonlySet<string> = new Set(
  Object.keys(RISK_SORT_FIELD_BY_COLUMN_ID),
);

export interface RiskSortRequestFields {
  readonly sortBy?: RiskScoreSortField;
  readonly sortOrder?: RiskScoreSortOrder;
}

/**
 * Maps the table's current sort (Stencil's `activeSortId` + `SortDirection`) to the request's
 * `sortBy`/`sortOrder`. Returns an empty object for the default order — no active column, an
 * unsortable column, or `SortDirection.None` — so the backend keeps its riskiest-first ordering.
 */
export function riskSortToRequest(
  activeSortId: string | undefined,
  sortDirection: SortDirection,
): RiskSortRequestFields {
  if (!activeSortId || sortDirection === SortDirection.None) return {};
  // eslint-disable-next-line security/detect-object-injection -- activeSortId is a column id from a fixed, developer-defined column set, not user input
  const sortBy = RISK_SORT_FIELD_BY_COLUMN_ID[activeSortId];
  if (!sortBy) return {};
  return {
    sortBy,
    sortOrder:
      sortDirection === SortDirection.Descending
        ? RiskScoreSortOrder.DESC
        : RiskScoreSortOrder.ASC,
  };
}

/**
 * The fixed set of risk-status filter values, in riskiest-first order. The service returns status
 * verbatim and matches the filter case-insensitively, and there is no SDK enum for it — so these
 * display-ready strings are the canonical filter values. Each carries the message key for its
 * localized chip label.
 */
export interface RiskStatusFilterOption {
  /** The canonical value sent in the `statuses` filter (matched case-insensitively). */
  readonly value: string;
  /** Message key for the localized chip label. */
  readonly messageKey: RiskStatusMessageKey;
}

export const RISK_STATUS_FILTER_OPTIONS: readonly RiskStatusFilterOption[] = [
  { value: 'Critical', messageKey: RISK_STATUS_MESSAGE_KEY.critical },
  { value: 'At Risk', messageKey: RISK_STATUS_MESSAGE_KEY.atRisk },
  { value: 'Watch', messageKey: RISK_STATUS_MESSAGE_KEY.watch },
  { value: 'Healthy', messageKey: RISK_STATUS_MESSAGE_KEY.healthy },
  { value: 'No Data', messageKey: RISK_STATUS_MESSAGE_KEY.noData },
];

/**
 * Maps category facets to filter options; value is the category, label carries its count.
 *
 * Tolerates an absent array: the GetRiskScoreFilterOptions response omits `categories` when the
 * request is unscoped (e.g. the desktop surface, which has no saved/preferred location, queries
 * without a location filter), so an undefined input yields an empty option list rather than a crash.
 */
export function toCategoryFilterOptions(
  categories: readonly RiskScoreFacetValue[] | undefined,
  formatLabel: RiskOptionLabel,
): RiskFilterOption[] {
  return (categories ?? []).map((facet) => ({
    label: formatLabel(facet.value, facet.count),
    value: facet.value,
  }));
}

/**
 * Maps metric facets to filter options; value is the stable metric code, label is the name.
 * Tolerates an absent array for the same reason as {@link toCategoryFilterOptions}.
 */
export function toMetricFilterOptions(
  metrics: readonly RiskScoreMetricFacet[] | undefined,
  formatLabel: RiskOptionLabel,
): RiskFilterOption[] {
  return (metrics ?? []).map((facet) => ({
    label: formatLabel(facet.displayName, facet.count),
    value: facet.metric,
  }));
}

/**
 * The fixed five status chips, each annotated with its current count (0 when the facet omits it,
 * since the full status list always renders). `statusLabel` localizes the status message key.
 * Tolerates an absent `statuses` array (see {@link toCategoryFilterOptions}).
 */
export function toStatusFilterOptions(
  statuses: readonly RiskScoreFacetValue[] | undefined,
  statusLabel: (messageKey: RiskStatusMessageKey) => string,
  formatLabel: RiskOptionLabel,
): RiskFilterOption[] {
  const countByValue = new Map(
    (statuses ?? []).map((facet) => [facet.value.trim().toUpperCase(), facet.count]),
  );
  return RISK_STATUS_FILTER_OPTIONS.map((option) => ({
    label: formatLabel(
      statusLabel(option.messageKey),
      countByValue.get(option.value.toUpperCase()) ?? 0,
    ),
    value: option.value,
  }));
}
